import React, { useState, useEffect } from 'react';
import { playertoolsAPI, playersAPI, teamsAPI, projectsAPI, getImageUrl } from '../../services/api';

const PlayerAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [formData, setFormData] = useState({
    plid: '',
    tid: '',
    pid: '',
    num: '',
    published: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedProject || selectedTeam) {
      fetchAssignments();
    }
  }, [selectedProject, selectedTeam]);

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes, projectsRes] = await Promise.all([
        playersAPI.getAll(),
        teamsAPI.getAll(),
        projectsAPI.getAll()
      ]);
      
      setPlayers(playersRes.data.players || playersRes.data);
      setTeams(teamsRes.data);
      setProjects(projectsRes.data.projects || projectsRes.data);
      
      if (!selectedProject && projectsRes.data.length > 0) {
        setSelectedProject(projectsRes.data[0]._id);
      }
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };  const fetchAssignments = async () => {
    try {
      const params = {};
      if (selectedProject) params.pid = selectedProject;
      if (selectedTeam) params.tid = selectedTeam;
      
      const response = await playertoolsAPI.getAll(params);
      const assignmentsData = response.data.playertools || response.data;
      
      // Sort by createdAt in descending order (newest first)
      const sortedAssignments = assignmentsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setAssignments(sortedAssignments);
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        plid: formData.plid,
        tid: formData.tid,
        pid: formData.pid,
        num: parseInt(formData.num) || 0,
        published: formData.published
      };

      if (editingAssignment) {
        await playertoolsAPI.update(editingAssignment._id, submitData);
      } else {
        await playertoolsAPI.create(submitData);
      }
      
      fetchAssignments();
      resetForm();
    } catch (err) {
      console.error('Error saving assignment:', err);
      setError(err.response?.data?.message || 'Failed to save assignment');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this player assignment?')) {
      try {
        await playertoolsAPI.delete(id);
        fetchAssignments();
      } catch (err) {
        setError('Failed to delete assignment');
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingAssignment(null);
    setFormData({
      plid: '',
      tid: selectedTeam || '',
      pid: selectedProject || '',
      num: '',
      published: true
    });
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      plid: assignment.plid?._id || '',
      tid: assignment.tid?._id || '',
      pid: assignment.pid?._id || '',
      num: assignment.num || '',
      published: assignment.published
    });
    setShowModal(true);
  };

  const handleNewAssignment = () => {
    setFormData({
      plid: '',
      tid: selectedTeam || '',
      pid: selectedProject || '',
      num: '',
      published: true
    });
    setShowModal(true);
  };

  const getNextAvailableNumber = () => {
    if (!selectedTeam || !selectedProject) return 1;
    
    const teamAssignments = assignments.filter(a => 
      a.tid?._id === selectedTeam && a.pid?._id === selectedProject
    );
    
    const usedNumbers = teamAssignments.map(a => a.num).filter(n => n > 0).sort((a, b) => a - b);
    
    for (let i = 1; i <= 99; i++) {
      if (!usedNumbers.includes(i)) {
        return i;
      }
    }
    return 1;
  };

  const groupedAssignments = assignments.reduce((acc, assignment) => {
    const projectName = assignment.pid?.name || 'Unknown Project';
    const teamName = assignment.tid?.name || 'Unknown Team';
    const key = `${projectName} - ${teamName}`;
    
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(assignment);
    return acc;
  }, {});

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Player Assignments</h2>
        <button
          className="btn btn-primary"
          onClick={handleNewAssignment}
          disabled={!selectedProject}
        >
          Assign Player
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Filter by Project</span>
          </label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="select select-bordered"
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project._id} value={project._id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">Filter by Team</span>
          </label>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="select select-bordered"
          >
            <option value="">All Teams</option>
            {teams.map((team) => (
              <option key={team._id} value={team._id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Assignments Display */}
      <div className="space-y-6">
        {Object.keys(groupedAssignments).length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No player assignments found for the selected filters.</p>
          </div>
        ) : (
          Object.entries(groupedAssignments).map(([groupKey, groupAssignments]) => (
            <div key={groupKey} className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">{groupKey}</h3>
              
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Jersey #</th>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupAssignments
                      .sort((a, b) => (a.num || 999) - (b.num || 999))
                      .map((assignment) => (
                      <tr key={assignment._id}>
                        <td>
                          <div className="badge badge-primary badge-lg">
                            {assignment.num || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="avatar">
                              <div className="w-10 h-10 rounded-full">
                                {assignment.plid?.misc?.portrait ? (
                                  <img 
                                    src={getImageUrl(assignment.plid.misc.portrait)}
                                    alt="Portrait"
                                    className="w-full h-full object-cover rounded-full"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-full">
                                    <span className="text-xs font-bold text-gray-500">
                                      {assignment.plid?.surname?.charAt(0)}{assignment.plid?.prename?.charAt(0) || ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="font-bold">
                                {assignment.plid?.surname || 'Unknown'}
                              </div>
                              <div className="text-sm opacity-50">
                                {assignment.plid?.prename || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {assignment.plid?.posid ? (
                            <div className="badge badge-outline">
                              {assignment.plid.posid.name}
                            </div>
                          ) : (
                            <span className="text-gray-500">No Position</span>
                          )}
                        </td>
                        <td>
                          <div className={`badge ${assignment.published ? 'badge-success' : 'badge-error'}`}>
                            {assignment.published ? 'Active' : 'Inactive'}
                          </div>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => handleEdit(assignment)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-error"
                              onClick={() => handleDelete(assignment._id)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingAssignment ? 'Edit Assignment' : 'Assign Player'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Project */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Project *</span>
                </label>
                <select
                  required
                  value={formData.pid}
                  onChange={(e) => setFormData({...formData, pid: e.target.value})}
                  className="select select-bordered"
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Team */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Team *</span>
                </label>
                <select
                  required
                  value={formData.tid}
                  onChange={(e) => setFormData({...formData, tid: e.target.value})}
                  className="select select-bordered"
                >
                  <option value="">Select Team</option>
                  {teams.map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Player */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Player *</span>
                </label>
                <select
                  required
                  value={formData.plid}
                  onChange={(e) => setFormData({...formData, plid: e.target.value})}
                  className="select select-bordered"
                >
                  <option value="">Select Player</option>
                  {players.map((player) => (
                    <option key={player._id} value={player._id}>
                      {player.surname}, {player.prename}
                    </option>
                  ))}
                </select>
              </div>

              {/* Jersey Number */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Jersey Number</span>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline"
                    onClick={() => setFormData({...formData, num: getNextAvailableNumber()})}
                  >
                    Auto
                  </button>
                </label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={formData.num}
                  onChange={(e) => setFormData({...formData, num: e.target.value})}
                  className="input input-bordered"
                  placeholder="Leave empty or use Auto button"
                />
              </div>

              {/* Published */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Active Assignment</span>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>

              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingAssignment ? 'Update' : 'Assign'} Player
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default PlayerAssignments;
